import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/navbar";
import {
  FiSearch,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiX,
  FiFilter,
  FiHelpCircle,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import crop2 from "../../assets/logos/crop2.png";
import crop44 from "../../assets/logos/crop44.png";
import milk from "../../assets/logos/milk.jpg";
import crop00 from "../../assets/logos/crop00.jpeg";
import crop88 from "../../assets/logos/crop88.png";
import crop90 from "../../assets/logos/crop90.jpg";
import { ShoppingCart, Check, AlertCircle, Loader, Verified, XCircle } from "lucide-react";
import api, { authAPI } from "../../services/api";
import "./marketplace.css";

// Sample product images for fallback
const productImages = [crop2, crop44, milk, crop00, crop88, crop90];

const getProductImage = (product) => {
  if (product?.image && product.image !== null) {
    return product.image;
  }
  if (product?.product_image && product.product_image !== null) {
    return product.product_image;
  }
  // Use product_id or id to consistently pick a fallback image
  const id = product?.product_id || product?.id || 0;
  return productImages[id % productImages.length];
};

const getProductName = (product) => {
  return product?.name || product?.product_name || 'Product';
};

// Alert Component
const Alert = ({ type, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`alert alert-${type}`}>
      <div className="alert-content">
        {type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
        <span>{message}</span>
      </div>
      <button onClick={onClose} className="alert-close">
        <FiX size={16} />
      </button>
    </div>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ isOpen, onClose, cart, total, onCheckout, user, openAuthModal }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [shippingAddress, setShippingAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if user is logged in and is a retailer
  useEffect(() => {
    if (isOpen && !authUser) {
      onClose();
      openAuthModal('checkout');
      return;
    }

    if (isOpen && authUser && authUser.role?.toLowerCase() !== 'retailer') {
      onClose();
      alert('Only retailers can purchase products. Please register as a retailer or login with a retailer account.');
      return;
    }
  }, [isOpen, authUser, navigate, onClose, cart, openAuthModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authUser || authUser.role?.toLowerCase() !== 'retailer') {
      alert('Only retailers can purchase products.');
      return;
    }

    if (!shippingAddress.trim() || !phone.trim()) {
      return;
    }
    setLoading(true);
    const checkoutData = {
      shipping_address: shippingAddress,
      phone: phone
    };
    console.log('Checkout data being sent:', checkoutData);
    await onCheckout(checkoutData);
    setLoading(false);
  };

  if (!isOpen) return null;

  // Don't show modal if user is not a retailer
  if (!authUser || authUser.role?.toLowerCase() !== 'retailer') {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content checkout-modal">
        <div className="modal-header">
          <h2>Checkout</h2>
          <button onClick={onClose} className="modal-close">
            <FiX size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="checkout-summary">
            <h3>Order Summary</h3>
            {cart.map((item) => (
              <div key={item.cart_id} className="checkout-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img
                    src={getProductImage(item.product)}
                    alt={getProductName(item.product)}
                    style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                  />
                  <span>{getProductName(item.product)}</span>
                </div>
                <span>{item.quantity} × ₦{parseFloat(item.price).toLocaleString()}</span>
                <span>₦{parseFloat(item.total).toLocaleString()}</span>
              </div>
            ))}
            <div className="checkout-total">
              <strong>Total: ₦{total.toLocaleString()}</strong>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="form-group">
              <label htmlFor="shipping-address">Shipping Address *</label>
              <textarea
                id="shipping-address"
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder="Enter your full shipping address"
                required
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                required
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? <Loader className="animate-spin" size={16} /> : null}
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Payment Overlay Component
const PaymentOverlay = ({ isOpen, onClose, authorizationUrl, onPaymentComplete }) => {
  useEffect(() => {
    if (isOpen && authorizationUrl) {
      console.log('Opening payment window with URL:', authorizationUrl);

      const paymentWindow = window.open(authorizationUrl, '_blank', 'width=500,height=600');

      if (!paymentWindow) {
        console.log('Popup blocked, redirecting in current window');
        window.location.href = authorizationUrl;
        return;
      }

      console.log('Payment window opened successfully');

      const checkClosed = setInterval(() => {
        if (paymentWindow.closed) {
          clearInterval(checkClosed);
          onPaymentComplete();
        }
      }, 1000);

      return () => {
        clearInterval(checkClosed);
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
      };
    }
  }, [isOpen, authorizationUrl, onPaymentComplete]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content payment-modal">
        <div className="payment-loading">
          <Loader className="animate-spin" size={32} />
          <h3>Processing Payment...</h3>
          <p>Payment window opened. Please complete your payment...</p>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const AuthModal = ({
  isOpen,
  onClose,
  authStep,
  setAuthStep,
  authTab,
  setAuthTab,
  authError,
  setAuthError,
  authSuccessMessage,
  loginForm,
  setLoginForm,
  onLoginSubmit,
  loading,
  registerForm,
  setRegisterForm,
  onRegisterSubmit,
  fieldErrors,
  onVerifySubmit
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content checkout-modal auth-modal-content">
        <div className="modal-header">
          <h2>{authStep === 'verify' ? 'Verify Email' : 'Login or Register'}</h2>
          <button onClick={onClose} className="modal-close">
            <FiX size={20} />
          </button>
        </div>

        {authStep !== 'verify' && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={() => { setAuthTab('login'); setAuthStep('login'); setAuthError(null); }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: authTab === 'login' ? '1px solid #0e4410' : '1px solid #ddd',
                background: authTab === 'login' ? '#e6f4ea' : '#fff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Login
            </button>
            <button
              onClick={() => { setAuthTab('register'); setAuthStep('register'); setAuthError(null); }}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: authTab === 'register' ? '1px solid #0e4410' : '1px solid #ddd',
                background: authTab === 'register' ? '#e6f4ea' : '#fff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Register (Retailer)
            </button>
          </div>
        )}

        {authError && (
          <div className="error-text" style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {authError}
          </div>
        )}
        {authSuccessMessage && (
          <div className="success-message" style={{
            backgroundColor: '#ecfdf3',
            border: '1px solid #86efac',
            color: '#166534',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Check size={16} /> {authSuccessMessage}
          </div>
        )}

        {authStep === 'login' && (
          <form onSubmit={onLoginSubmit} className="checkout-form" style={{ marginTop: '20px' }}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? <Loader className="animate-spin" size={16} /> : null}
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          </form>
        )}

        {authStep === 'register' && (
          <form onSubmit={onRegisterSubmit} className="checkout-form" style={{ marginTop: '20px' }}>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={registerForm.first_name}
                onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                required
              />
              {fieldErrors.first_name && <small className="field-error">{fieldErrors.first_name}</small>}
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={registerForm.last_name}
                onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                required
              />
              {fieldErrors.last_name && <small className="field-error">{fieldErrors.last_name}</small>}
            </div>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={registerForm.user_name}
                onChange={(e) => setRegisterForm({ ...registerForm, user_name: e.target.value })}
                required
              />
              {fieldErrors.user_name && <small className="field-error">{fieldErrors.user_name}</small>}
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                required
              />
              {fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
              {fieldErrors.password && <small className="field-error">{fieldErrors.password}</small>}
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={registerForm.password_confirm}
                  onChange={(e) => setRegisterForm({ ...registerForm, password_confirm: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? <Loader className="animate-spin" size={16} /> : null}
                {loading ? 'Registering...' : 'Register as Retailer'}
              </button>
            </div>
          </form>
        )}

        {authStep === 'verify' && (
          <form onSubmit={onVerifySubmit} className="checkout-form" style={{ marginTop: '20px' }}>
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                value={registerForm.otp}
                onChange={(e) => setRegisterForm({ ...registerForm, otp: e.target.value.replace(/[^0-9]/g, '').slice(0, 6) })}
                placeholder="Enter 6-digit code"
                required
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => { setAuthStep('login'); setAuthTab('login'); }} className="btn-secondary">
                Back to Login
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? <Loader className="animate-spin" size={16} /> : null}
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Marketplace() {
  const navigate = useNavigate();
  const { user, login, register, verifyEmailOrPhone } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [priceSort, setPriceSort] = useState("none"); // none, low-to-high, high-to-low
  const [verificationFilter, setVerificationFilter] = useState("all"); // all, verified, unverified
  const [showFilters, setShowFilters] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [addingToCart, setAddingToCart] = useState({});
  const [updatingQuantity, setUpdatingQuantity] = useState({});
  const [deletingFromCart, setDeletingFromCart] = useState({});
  const [categories, setCategories] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // login | register
  const [authStep, setAuthStep] = useState("login"); // login | register | verify
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authFieldErrors, setAuthFieldErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null); // checkout | add_to_cart
  const [pendingProduct, setPendingProduct] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    first_name: "",
    last_name: "",
    user_name: "",
    email: "",
    password: "",
    password_confirm: "",
    otp: "",
  });
  const [authSuccessMessage, setAuthSuccessMessage] = useState(null);

  // No productImages here now

  const showAlertMessage = (type, message) => {
    setAlert({ type, message });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    if (user && user.role?.toLowerCase() === 'retailer') {
      fetchCart();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.getRetailerProducts();
      if (response.success) {
        // Filter products to only show those with status "approved"
        const approvedProducts = response.data.filter(product =>
          product.status === 'approved' || product.status === 'Approved'
        );
        setProducts(approvedProducts);
        setFilteredProducts(approvedProducts);
      } else {
        showAlertMessage('error', 'Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      showAlertMessage('error', 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await authAPI.getCategories();
      if (response) {
        setCategories(response);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await api.getRetailerCart();
      if (response.success) {
        setCart(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product => {
        const productName = product.name || product.description || 'Product';
        return (
          productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (product.farmer && product.farmer.name && product.farmer.name.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      });
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(product =>
        product.category_name === selectedCategory || product.category === selectedCategory
      );
    }

    // Verification filter
    if (verificationFilter === "verified") {
      filtered = filtered.filter(product => product.is_verified === true || product.verified === true);
    } else if (verificationFilter === "unverified") {
      filtered = filtered.filter(product => !product.is_verified && !product.verified);
    }

    // Price sort
    if (priceSort === "low-to-high") {
      filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (priceSort === "high-to-low") {
      filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategory, priceSort, verificationFilter]);

  // No getProductImage here now

  const addToCart = async (product, overrideUser = null) => {
    const currentUser = overrideUser || user;

    // Check if user is logged in and is a retailer
    if (!currentUser) {
      setPendingAction('add_to_cart');
      setPendingProduct(product);
      setShowAuthModal(true);
      setAuthTab("login");
      setAuthStep("login");
      return;
    }

    if (currentUser.role?.toLowerCase() !== 'retailer') {
      showAlertMessage('error', 'Only retailers can purchase products. Please register as a retailer or login with a retailer account.');
      return;
    }

    if (addingToCart[product.product_id]) return;

    try {
      setAddingToCart(prev => ({ ...prev, [product.product_id]: true }));

      const response = await api.addToCart({
        product_id: product.product_id,
        quantity: 1
      });

      if (response.success) {
        showAlertMessage('success', 'Item added to cart successfully!');
        fetchCart();
      } else {
        showAlertMessage('error', response.message || 'Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showAlertMessage('error', 'Failed to add item to cart');
    } finally {
      setAddingToCart(prev => ({ ...prev, [product.product_id]: false }));
    }
  };

  const handleCheckout = async (checkoutData) => {
    if (!user || user.role?.toLowerCase() !== 'retailer') {
      setPendingAction('checkout');
      setShowAuthModal(true);
      setAuthTab("login");
      setAuthStep("login");
      return;
    }

    try {
      const response = await api.checkout(checkoutData);
      console.log('Checkout response:', response);

      if (response.success) {
        const { authorization_url, reference } = response.data;
        console.log('Payment details:', { authorization_url, reference });

        if (!authorization_url) {
          showAlertMessage('error', 'Payment URL not provided');
          return;
        }

        setPaymentUrl(authorization_url);
        setPaymentReference(reference);
        localStorage.setItem('checkout_reference', reference);

        setShowCheckoutModal(false);
        setShowPaymentOverlay(true);

        pollPaymentVerification(reference);
      } else {
        showAlertMessage('error', response.message || 'Checkout failed');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      showAlertMessage('error', 'Checkout failed');
    }
  };

  const pollPaymentVerification = useCallback(async (reference) => {
    const maxAttempts = 30;
    let attempts = 0;

    const pollInterval = setInterval(async () => {
      attempts++;

      try {
        const verifyResponse = await api.verifyPayment(reference);

        if (verifyResponse.success) {
          clearInterval(pollInterval);
          localStorage.removeItem('checkout_reference');
          setPaymentReference('');
          setShowPaymentOverlay(false);

          showAlertMessage('success', 'Order placed successfully!');
          setCart([]);
          fetchCart();
          // Redirect to success page
          navigate("/marketplace/success");
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        if (err.response?.status === 400 || err.response?.status === 422) {
          clearInterval(pollInterval);
          localStorage.removeItem('checkout_reference');
          setPaymentReference('');
          setShowPaymentOverlay(false);
          showAlertMessage('error', 'Payment could not be completed. Please try again.');
        }
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        localStorage.removeItem('checkout_reference');
        setPaymentReference('');
        setShowPaymentOverlay(false);
        showAlertMessage('error', 'Payment verification timeout. Please check your order status.');
      }
    }, 10000);
  }, [fetchCart, showAlertMessage, navigate]);

  const handlePaymentComplete = async () => {
    // Handled by polling
  };

  // Check for pending payment verification on component mount
  useEffect(() => {
    const pendingReference = localStorage.getItem('checkout_reference');
    if (pendingReference) {
      setPaymentReference(pendingReference);
      setShowPaymentOverlay(true);
      pollPaymentVerification(pendingReference);
    }
  }, [pollPaymentVerification]);

  const totalAmount = cart.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);

  const getCartItemQuantity = (productId) => {
    const cartItem = cart.find(item => item.product && item.product.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  const openAuthModal = (action) => {
    setPendingAction(action);
    setShowAuthModal(true);
    setAuthTab("login");
    setAuthStep("login");
    setAuthError(null);
    setAuthFieldErrors({});
    setAuthSuccessMessage(null);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setAuthStep(authTab === "login" ? "login" : "register");
    setAuthError(null);
    setAuthFieldErrors({});
    setAuthSuccessMessage(null);
    setPendingAction(null);
    setPendingProduct(null);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    setAuthFieldErrors({});
    try {
      const result = await login({ email: loginForm.email, password: loginForm.password });
      if (result.success) {
        setShowAuthModal(false);
        if (pendingAction === 'checkout') {
          navigate('/marketplace/cart');
        } else if (pendingAction === 'add_to_cart' && pendingProduct) {
          await addToCart(pendingProduct, result.user);
        } else {
          navigate('/marketplace');
        }
      } else {
        setAuthError(result.message || 'Login failed');
      }
    } catch (err) {
      setAuthError(err?.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    setAuthFieldErrors({});
    if (registerForm.password !== registerForm.password_confirm) {
      setAuthError('Passwords do not match');
      setAuthLoading(false);
      return;
    }
    try {
      const payload = {
        first_name: registerForm.first_name,
        last_name: registerForm.last_name,
        user_name: registerForm.user_name,
        password: registerForm.password,
        password_confirm: registerForm.password_confirm,
        role: 'Retailer',
        verification_type: 'email',
        email: registerForm.email,
      };
      const result = await register(payload);
      if (result.success) {
        setAuthSuccessMessage('Registration successful. Enter the code sent to your email.');
        setAuthStep('verify');
      } else {
        if (result.errors) {
          setAuthFieldErrors(result.errors);
        }
        setAuthError(result.message || 'Registration failed');
      }
    } catch (err) {
      setAuthError(err?.message || 'Registration failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    try {
      const otp = registerForm.otp?.trim();
      if (!otp || otp.length !== 6) {
        setAuthError('Enter a valid 6-digit code');
        setAuthLoading(false);
        return;
      }
      const verifyResult = await verifyEmailOrPhone(otp, registerForm.email);
      if (verifyResult.status || verifyResult.success) {
        setAuthSuccessMessage('Verification successful. Logging you in...');
        const loginResult = await login({ email: registerForm.email, password: registerForm.password });
        if (loginResult.success) {
          setShowAuthModal(false);
          if (pendingAction === 'checkout') {
            navigate('/marketplace/cart');
          } else if (pendingAction === 'add_to_cart' && pendingProduct) {
            await addToCart(pendingProduct, loginResult.user);
          } else {
            navigate('/marketplace');
          }
        } else {
          setAuthError(loginResult.message || 'Login after verification failed. Please login manually.');
          setAuthStep('login');
          setAuthTab('login');
        }
      } else {
        setAuthError(verifyResult.message || 'Verification failed');
      }
    } catch (err) {
      setAuthError(err?.message || 'Verification failed');
    } finally {
      setAuthLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="marketplace-page" style={{ padding: '2rem', textAlign: 'center' }}>
        <Loader className="animate-spin" size={32} />
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="marketplace-page">
      <Navbar />
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={hideAlert}
        />
      )}

      <div className="marketplace-header">
        <h1>Marketplace</h1>
        <p>Buy quality farm products from verified farmers</p>
      </div>

      {/* Top Bar with Search, Filters, and Cart */}
      <div className="marketplace-controls">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Verification Filter Tabs */}
        <div className="filter-tabs">
          <button
            onClick={() => setVerificationFilter('all')}
            className={`filter-tab-btn ${verificationFilter === 'all' ? 'active' : ''}`}
          >
            All Products
          </button>
          <button
            onClick={() => setVerificationFilter('verified')}
            className={`filter-tab-btn ${verificationFilter === 'verified' ? 'active' : ''}`}
          >
            <Verified size={16} />
            Verified
          </button>
          <button
            onClick={() => setVerificationFilter('unverified')}
            className={`filter-tab-btn ${verificationFilter === 'unverified' ? 'active' : ''}`}
          >
            <XCircle size={16} />
            Unverified
          </button>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="help-btn"
            title="What are verified products?"
          >
            <FiHelpCircle size={20} />
          </button>
        </div>

        {/* Cart Button */}
        {user && user.role?.toLowerCase() === 'retailer' && (
          <button
            onClick={() => navigate('/marketplace/cart')}
            className="cart-btn"
          >
            <ShoppingCart size={20} />
            <span>Cart</span>
            {cartItemCount > 0 && (
              <span className="cart-badge">
                {cartItemCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Help Tooltip */}
      {showHelp && (
        <div className="help-tooltip">
          <h4>What are Verified Products?</h4>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            Verified products are products that have been checked and approved by the admin, confirming that they are real and available, while unverified products are products that have not yet been reviewed or approved by the admin.
          </p>
        </div>
      )}

      {/* Filters Sidebar */}
      <div className="marketplace-content">
        <div className="filters-sidebar">
          <h3 className="sidebar-title">Filters</h3>

          {/* Category Filter */}
          <div className="filter-group">
            <label className="filter-label">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name || cat.category_name}>
                  {cat.name || cat.category_name}
                </option>
              ))}
            </select>
          </div>

          {/* Price Sort */}
          <div className="filter-group">
            <label className="filter-label">Sort by Price</label>
            <select
              value={priceSort}
              onChange={(e) => setPriceSort(e.target.value)}
              className="filter-select"
            >
              <option value="none">No sorting</option>
              <option value="low-to-high">Price: Low to High</option>
              <option value="high-to-low">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Products Grid */}
        <div className="products-section">
          <div className="products-grid">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div key={product.product_id} className="product-card">
                  {/* Verification Badge */}
                  {(product.is_verified || product.verified) && (
                    <div className="verified-badge">
                      <Verified size={14} />
                      Verified
                    </div>
                  )}

                  {getCartItemQuantity(product.product_id) > 0 && (
                    <div className="qty-badge">
                      {getCartItemQuantity(product.product_id)}
                    </div>
                  )}

                  <div className="product-image-container">
                    <img
                      src={getProductImage(product)}
                      alt={getProductName(product)}
                      className="product-image"
                    />
                  </div>
                  <div className="product-details">
                    <h3 className="product-title">{getProductName(product)}</h3>
                    <p className="farmer-name">
                      By {product.farmer?.name || product.farmer?.userName || 'Unknown Farmer'}
                    </p>
                    <p className="product-price">
                      ₦{parseFloat(product.price).toLocaleString()} <span style={{ fontSize: '0.8em', fontWeight: 'normal', color: '#666' }}>/ {product.unit}</span>
                    </p>
                    <p className="product-stock">
                      {product.quantity_available} {product.unit}s available
                    </p>
                    <button
                      onClick={() => addToCart(product)}
                      disabled={product.quantity_available <= 0 || addingToCart[product.product_id]}
                      className="add-btn"
                    >
                      {addingToCart[product.product_id] ? (
                        <>
                          <Loader className="animate-spin" size={16} />
                          Adding...
                        </>
                      ) : product.quantity_available <= 0 ? (
                        'Out of Stock'
                      ) : (
                        <>+ Add to Cart</>
                      )}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-products">
                No products found matching your criteria
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        cart={cart}
        total={totalAmount}
        onCheckout={handleCheckout}
        user={user}
        openAuthModal={openAuthModal}
      />

      {/* Payment Overlay */}
      <PaymentOverlay
        isOpen={showPaymentOverlay}
        onClose={() => setShowPaymentOverlay(false)}
        authorizationUrl={paymentUrl}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        authStep={authStep}
        setAuthStep={setAuthStep}
        authTab={authTab}
        setAuthTab={setAuthTab}
        authError={authError}
        setAuthError={setAuthError}
        authSuccessMessage={authSuccessMessage}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        onLoginSubmit={handleLoginSubmit}
        loading={authLoading}
        registerForm={registerForm}
        setRegisterForm={setRegisterForm}
        onRegisterSubmit={handleRegisterSubmit}
        fieldErrors={authFieldErrors}
        onVerifySubmit={handleVerifySubmit}
      />
    </div>
  );
}
